import { createPortal } from 'react-dom';
import { Loader2, Sparkles } from 'lucide-react';

const SESSION_END_EVOLUIR_MIN_WAIT_MS = 1_000;

type SessionEndReviewConfirmModalProps = {
	isOpen: boolean;
	isSaving: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	/** Demo/tutorial: marca o modal para coaches do tutorial. */
	demoMode?: boolean;
};

/**
 * Modal de confirmação positivo do fluxo de revisão pós-sessão.
 * Diferente do ConfirmActionModal (que tem ícone de alerta e "Cancelar" fixo),
 * aqui usamos um tom de confirmação positivo com botões "evoluir"/"voltar".
 */
const SessionEndReviewConfirmModal = ({
	isOpen,
	isSaving,
	onConfirm,
	onCancel,
	demoMode = false,
}: SessionEndReviewConfirmModalProps) => {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[10030] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={isSaving ? undefined : onCancel}
				aria-hidden
			/>
			<div
				className="animate-in zoom-in-95 relative w-full max-w-[400px] rounded-[2rem] bg-white p-8 text-center shadow-2xl duration-200"
				{...(demoMode
					? { 'data-tutorial-demo': 'review-evoluir-modal' }
					: {})}
			>
				<div className="mb-4 flex justify-center">
					<div className="rounded-full bg-[#8ab4f8]/15 p-4 text-[#3b6fd4]">
						{isSaving ? (
							<Loader2
								size={32}
								strokeWidth={2.5}
								aria-hidden
								className="animate-spin"
							/>
						) : (
							<Sparkles size={32} strokeWidth={2.5} aria-hidden />
						)}
					</div>
				</div>

				<h3 className="mb-2 text-xl font-black text-stone-900">
					Evoluir prontuário?
				</h3>

				<p className="mb-8 text-sm font-medium leading-relaxed text-stone-500">
					{isSaving
						? 'Aplicando suas edições ao prontuário do paciente...'
						: 'Suas edições serão aplicadas ao prontuário do paciente.'}
				</p>

				<div className="flex flex-col gap-3">
					<button
						type="button"
						onClick={onConfirm}
						disabled={isSaving}
						className="flex w-full items-center justify-center gap-2 rounded-full border-none bg-zinc-900 py-3 font-bold text-white shadow-md transition-colors hover:bg-zinc-800 disabled:cursor-default disabled:bg-zinc-800 disabled:opacity-60"
					>
						{isSaving ? (
							'Evoluindo...'
						) : (
							<>
								<Sparkles
									className="size-4"
									strokeWidth={2.5}
									aria-hidden
								/>
								Evoluir
							</>
						)}
					</button>

					<button
						type="button"
						onClick={onCancel}
						disabled={isSaving}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
					>
						Voltar
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};

export { SessionEndReviewConfirmModal, SESSION_END_EVOLUIR_MIN_WAIT_MS };
